/**
 * stoq-notify.js — the ONLY file that knows about StoQ (Restock Rocket).
 *
 * Bridges our app-agnostic <buy-box> to StoQ's back-in-stock "Notify me" modal:
 *   buy-box dispatches `sundae:notify-open` { variantId, handle }  →  we point StoQ at that product
 *   (only when it isn't already there) and open its modal for that variant.
 * Also HIDES StoQ's own float button — we trigger the flow from the sold-out size buttons instead.
 *
 * Loads wherever a <buy-box> renders — blocks/buy-box.liquid (the new PDP) AND
 * snippets/quick-view-drawer.liquid (so any host of the drawer works without extra wiring). Never on
 * the legacy PDP, which is what keeps the float-button hide rule off the page customers see today.
 * Both tags on one page is normal and safe — see the idempotence guard at the top of the IIFE.
 * If StoQ is ever swapped for a theme-owned back-in-stock, replace ONLY this file (the buy-box never
 * names StoQ; the `sundae:notify-open` contract stays the same).
 *
 * Re-verify + gotchas: docs/00-features/product/brainstorm/StoQ.md
 *   - openModal(productData, variantId): variantId MUST be numeric (a string is silently ignored,
 *     and the modal falls back to the first sold-out size).
 *   - the float button is injected at <body> level, asynchronously → hide it with an injected rule.
 *   - `stoq:loaded` fires when the API is ready (readiness gate; don't poll).
 *   - StoQ IS loaded and `enabled: true` even on a 100%-in-stock PDP (verified live 2026-09-03) —
 *     it just renders no button. So readiness is never the reason a notify click does nothing;
 *     the product context is. `updateProductContext(handle)` re-points it, both directions.
 *
 * NON-PRODUCT PAGES (the quick-view drawer on the promo page — or a collection page later).
 * StoQ's own loader (its app embed) picks a script per page type: on a product page its PRODUCT
 * script (openModal + updateProductContext…); on index / collection / search / page its COLLECTION
 * script — but ONLY if the admin's "show button on <that page type>" is on. It's off for all of them
 * (checked live 2026-09-21), so a regular page gets NO StoQ script, yet `stoq:loaded` still fires —
 * the notify click used to wait on an event that had already happened and do nothing.
 * Here, on a non-product page we load StoQ's COLLECTION script ourselves (its URL is published in
 * _RestockRocketConfig; same build choice as their loader), once, on demand — and warm it when a
 * drawer shows a product with a sold-out size. That script exposes openModal(productData, variantId)
 * — StoQ's documented call for custom quick-add buttons — and, with "show button on page" off, it
 * injects no buttons of its own (gated in its code). No product context to point at on these pages,
 * so the product data is passed in (/products/<handle>.js — which `theme dev` 401s: test on a hosted
 * preview). Loading their script by hand is not in StoQ's docs; openModal is.
 * On a product page none of this runs — the path above is unchanged.
 */
(function () {
  // Idempotence guard. This file is loaded BOTH by blocks/buy-box.liquid (the PDP) and by
  // snippets/quick-view-drawer.liquid (so any host of the drawer — a collection page — works without
  // extra wiring). On the PDP both render, and two <script src> tags with the same URL EXECUTE twice
  // even though the browser fetches once. Without this, the page would end up with two
  // `sundae:notify-open` listeners: every notify click would run the whole flow twice, firing two
  // updateProductContext calls that race each other and two `stoq:modal:opened` analytics events.
  //
  // buy-box.js and ss-accordion.js get this for free from `customElements.get(...)`; this file
  // defines no element, so it needs an explicit flag.
  if (window.__ssStoqNotify) return;
  window.__ssStoqNotify = true;

  // Hide StoQ's default float button. An injected rule catches it however / whenever StoQ renders it
  // (added to <body> async), and exists only on pages that load this file — legacy PDP untouched.
  var css = document.createElement('style');
  css.textContent = '.restock-rocket-button-container-float-right { display: none !important; }';
  document.head.appendChild(css);

  function currentHandle() {
    var cfg = window._RestockRocketConfig;
    return cfg && cfg.product ? cfg.product.handle : null;
  }

  function api() {
    var rr = window._RestockRocket;
    return rr && typeof rr.openModal === 'function' ? rr : null;
  }

  // The PAGE's own product. StoQ boots pointed at it; we record it the first time we point AWAY, so
  // the context can be warmed back after the quick-view drawer has borrowed it.
  var pageHandle = null;

  // The one context switch currently in flight, as { handle, promise }. Deduping matters: the drawer
  // warms the context on open, and a shopper who clicks a size DURING that fetch must join the same
  // request rather than start a second one — two concurrent updateProductContext calls would race to
  // set the config, and the loser could leave StoQ on the wrong product.
  var pending = null;

  /**
   * Point StoQ at `handle`, resolving when it is actually there (or when we've given up).
   * Cheap and synchronous when it is already there — which is why the PDP's own notify path is
   * unchanged by any of this.
   */
  function pointAt(handle) {
    var rr = api();
    if (!rr || !handle || typeof rr.updateProductContext !== 'function') return Promise.resolve();
    if (currentHandle() === handle) return Promise.resolve();
    if (pending && pending.handle === handle) return pending.promise;

    if (pageHandle === null) pageHandle = currentHandle();

    // Async — it re-fetches /products/<handle>.js (measured ~870ms cold, ~385ms on a later switch).
    // Promise.resolve() wraps it in case a future version returns a non-thenable.
    var promise = Promise.resolve(rr.updateProductContext(handle))
      .catch(function () {})
      .then(function () {
        if (pending && pending.handle === handle) pending = null;
      });
    pending = { handle: handle, promise: promise };
    return promise;
  }

  function openWithCurrentContext(variantId) {
    var cfg = window._RestockRocketConfig;
    // ⚠️ numeric id — a string variantId is silently ignored.
    window._RestockRocket.openModal(cfg && cfg.product ? cfg.product : null, Number(variantId));
  }

  // ---- Non-product pages (see the header) ---------------------------------------------------

  // StoQ writes pageType into its config from its inline app-embed script, before this deferred file
  // runs. Absent config (StoQ off / uninstalled) → false → the unchanged path below → a graceful no-op.
  function onRegularPage() {
    var cfg = window._RestockRocketConfig;
    return !!(cfg && cfg.pageType && cfg.pageType !== 'product');
  }

  var pageScript = null;

  // The same build choice StoQ's loader makes: its "preorder" build when preorder / selling plans are
  // in play, else the lighter "bis" build.
  function collectionScriptUrl(cfg) {
    var settings = cfg.cachedSettings || {};
    var plans = Array.isArray(cfg.sellingPlans) ? cfg.sellingPlans : [];
    var preorderBuild =
      settings.preorder_enabled ||
      plans.some(function (plan) { return plan && plan.enabled; }) ||
      (Array.isArray(cfg.disabledSellingPlanIds) && cfg.disabledSellingPlanIds.length > 0);
    return preorderBuild ? cfg.scriptUrlCollection : cfg.scriptUrlCollectionBis;
  }

  // Resolves once window._RestockRocket.openModal exists — already there if the admin ever turns on
  // "show button on page" (StoQ then loads this script itself).
  function ensurePageScript() {
    if (api()) return Promise.resolve();
    if (pageScript) return pageScript;

    var url = collectionScriptUrl(window._RestockRocketConfig || {});
    if (!url) return Promise.reject(new Error('no StoQ collection script URL'));

    pageScript = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = url;
      script.defer = true;
      script.onerror = function () { reject(new Error('StoQ collection script failed to load')); };
      script.onload = function () {
        // openModal may be attached a beat after the file runs — give it up to 5s.
        var waited = 0;
        (function check() {
          if (api()) return resolve();
          if ((waited += 50) > 5000) return reject(new Error('StoQ openModal never appeared'));
          setTimeout(check, 50);
        })();
      };
      document.body.appendChild(script);
    }).catch(function (err) {
      pageScript = null; // let a later click try again
      throw err;
    });
    return pageScript;
  }

  var products = {};

  // The product as Shopify's AJAX API serves it — a superset of what openModal reads (id, title,
  // handle, variants). Market-aware via Shopify.routes.root.
  function productData(handle) {
    if (!products[handle]) {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      products[handle] = fetch(root + 'products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
        .then(function (res) {
          if (!res.ok) throw new Error('product ' + res.status);
          return res.json();
        })
        .catch(function (err) {
          delete products[handle];
          throw err;
        });
    }
    return products[handle];
  }

  function openOnPage(variantId, handle) {
    Promise.all([ensurePageScript(), productData(handle)])
      .then(function (parts) {
        // ⚠️ numeric id — a string variantId is silently ignored.
        window._RestockRocket.openModal(parts[1], Number(variantId));
      })
      .catch(function (err) {
        console.warn('[stoq-notify] could not open StoQ on this page (' + err.message + '). On `shopify theme dev` /products/<handle>.js answers 401 — test on a hosted preview.');
      });
  }

  function openModalFor(variantId, handle) {
    if (handle && onRegularPage()) {
      openOnPage(variantId, handle);
      return;
    }

    var rr = api();
    if (!rr) {
      // StoQ not ready yet → retry once when it signals readiness. If StoQ isn't installed at all,
      // `stoq:loaded` never fires and we simply no-op (graceful — the modal just doesn't open).
      window.addEventListener('stoq:loaded', function () { openModalFor(variantId, handle); }, { once: true });
      return;
    }

    // StoQ's config is pinned to the PAGE's product, but the quick-view drawer asks on behalf of a
    // DIFFERENT one. Re-point it first or the modal is wrong: with a sold-out PDP it opened showing
    // the PDP's product, and with a fully-stocked PDP it opened nothing at all — StoQ had no sold-out
    // variant to render. Both verified live 2026-09-03.
    //
    // Setting the context per REQUEST rather than trusting the warm-up is what keeps this correct:
    // the warm-up is only a head start, and if it hasn't landed (or never ran) this still waits for
    // the right product before opening.
    pointAt(handle).then(function () {
      // ⚠️ StoQ RESOLVES `false` when it can't load the product — it does NOT reject, so a .catch()
      // alone would never see the failure. Check the context actually moved.
      //
      // It re-fetches /products/<handle>.js, which `shopify theme dev` answers with 401 — so this
      // ALWAYS fails on the local dev server and the drawer's notify looks broken there even when
      // the code is right. Verify this flow on a hosted preview, not dev.
      if (handle && currentHandle() !== handle) {
        console.warn('[stoq-notify] could not switch StoQ to "' + handle + '" — opening with the page\'s product instead. On `shopify theme dev` this is expected (401 on /products/<handle>.js); test on a hosted preview.');
      }
      // Open either way: with the right context this is the correct modal; without it, the page's
      // product — the old bug, but a silent dead click is worse.
      openWithCurrentContext(variantId);
    });
  }

  // ---- Warm-up ------------------------------------------------------------------------------
  // A buy box for a sold-out product just appeared (the quick-view drawer, today). Fetch its context
  // NOW, while the shopper is still reading, so the eventual size click costs ~0 instead of ~870ms.
  // Fire-and-forget: if it fails or is still running when they click, openModalFor waits on the very
  // same promise, so this can only ever make things faster, never wrong.
  document.addEventListener('sundae:buy-box-ready', function (e) {
    var detail = e.detail || {};
    if (!detail.hasSoldOutVariant || !detail.handle) return;
    if (onRegularPage()) {
      // Non-product page: pre-load StoQ's script + this product's data instead (no context to point).
      ensurePageScript().catch(function () {});
      productData(detail.handle).catch(function () {});
      return;
    }
    pointAt(detail.handle);
  });

  // ...and warm it BACK to the page's product when the drawer closes, so a shopper who browses a
  // drawer and then uses the PDP's own notify doesn't pay the switch a second time. Correctness never
  // depends on this — openModalFor re-points regardless; it only moves the cost off the click.
  // Watched via the drawer's public `is-open` class rather than a new event, to keep quick-view.js
  // unaware of back-in-stock entirely.
  function watchDrawer() {
    var drawer = document.querySelector('quick-view-drawer');
    if (!drawer) return;
    var wasOpen = drawer.classList.contains('is-open');
    new MutationObserver(function () {
      var isOpen = drawer.classList.contains('is-open');
      if (wasOpen && !isOpen && pageHandle) pointAt(pageHandle);
      wasOpen = isOpen;
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchDrawer);
  else watchDrawer();

  document.addEventListener('sundae:notify-open', function (e) {
    var id = e.detail && e.detail.variantId;
    // handle may be absent (older markup / another future caller) — then we just use whatever context
    // StoQ already has, which is exactly the pre-fix behaviour.
    var handle = (e.detail && e.detail.handle) || null;
    if (id != null) openModalFor(id, handle);
  });
})();
